import {
	ColumnMeta,
	flexRender,
	Header,
	type ReactTable,
	type RowData,
	type TableFeatures,
} from "@tanstack/react-table";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
	ErrorRow,
	SkeletonRows,
	NoResultsRow,
} from "./table";
import {
	SortDown,
	SortFilter,
	SortUp,
	ChevronDown,
} from "@/assets/icons/Icons";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "./separator";
import React, { useState } from "react";
import { DotIcon } from "lucide-react";
import type { CommonFeatures } from "@/hooks/useTanstackTable";



interface DataTableProps<TFeatures extends TableFeatures, TData extends RowData> {
	readonly table: ReactTable<TFeatures, TData>;
	readonly noResultsMessage: string;
	readonly errorMessage: string;
	readonly isLoading: boolean;
	readonly isError: boolean;
}
export function DataTable<TFeatures extends CommonFeatures, TData extends RowData>(props: DataTableProps<TFeatures, TData>) {
	const { noResultsMessage, errorMessage, isLoading, isError } = props;
	const table = props.table as unknown as ReactTable<CommonFeatures, TData>;
	const isSuccess = !isLoading && !isError;
	const maxColSpan = table.getVisibleLeafColumns().length;
	const { pagination } = table.state;
	const skeletonRows = pagination.pageSize == -1 ? 10 : pagination.pageSize; // Default to 10 if pageSize is not set
	const isEmpty = isSuccess && table.getRowModel().rows.length === 0;

	return (
		<Table className="w-full">
			<TableHeader>
				{table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={headerGroup.id}>
						{headerGroup.headers.map((header) =>
							header.column.getCanFilter() ? (
								<FilteredHead header={header} key={header.id} />
							) : (
								<NormalHead header={header} key={header.id} />
							),
						)}
					</TableRow>
				))}
			</TableHeader>
			<TableBody className="text-center">
				{isError && <ErrorRow message={errorMessage} colSpan={maxColSpan} />}
				{isLoading && <SkeletonRows rows={skeletonRows} colSpan={maxColSpan} />}
				{isEmpty && (
					<NoResultsRow message={noResultsMessage} colSpan={maxColSpan} />
				)}

				{isSuccess &&
					table.getRowModel().rows.map((row) => (
						<TableRow key={row.id}>
							{row.getVisibleCells().map((cell) => (
								<TableCell
									key={cell.id}
									style={{ width: `${cell.column.columnDef.size}%` }}
								>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</TableCell>
							))}
						</TableRow>
					))}
			</TableBody>
		</Table>
	);
}

function FilteredHead<TData extends RowData>({ header }: { readonly header: Header<CommonFeatures, TData>; }) {
	const [open, setOpen] = useState(false);

	const canFilter =
		header.column.getCanFilter() && header.column.columnDef.meta?.filterVariant;

	const sortTo = header.column.getIsSorted() || "none";
	const filteredValue = header.column.getFilterValue();

	let SortIcon;
	if (filteredValue == null) {
		switch (sortTo) {
			case "asc":
				SortIcon = SortUp;
				break;
			case "desc":
				SortIcon = SortDown;
				break;
			case "none":
				SortIcon = ChevronDown;
		}
	} else {
		switch (sortTo) {
			case "asc":
				SortIcon = SortUp;
				break;
			case "desc":
				SortIcon = SortDown;
				break;
			case "none":
				SortIcon = SortFilter;
		}
	}
	const onToggleSort = (value: "asc" | "desc" | "none") => {
		return () => {
			if (value === sortTo) {
				header.column.clearSorting();
			} else if (value === "asc") {
				header.column.toggleSorting(false);
			} else if (value === "desc") {
				header.column.toggleSorting(true);
			} else {
				header.column.clearSorting();
			}
			setOpen(false);
		};
	};

	const setFilterSelect = (value: string) => {
		if (value === "none" || value == filteredValue) {
			header.column.setFilterValue(null);
		} else {
			header.column.setFilterValue(value);
		}
		header.column.clearSorting();

		setOpen(false);
	};

	return (
		<TableHead colSpan={header.colSpan}>
			{header.isPlaceholder ? null : (
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger
						onAuxClick={(e) => {
							setOpen(true);
							e.preventDefault();
						}}
					>
						<div className="cursor-pointer select-none flex justify-center gap-3">
							{flexRender(header.column.columnDef.header, header.getContext())}
							<SortIcon style={{ padding: "1px" }} />
						</div>
					</PopoverTrigger>
					<PopoverContent>
						<SortOptions onToggleSort={onToggleSort} sortTo={sortTo} />
						{canFilter == "select" && (
							<SelectFilterOptions
								options={header.column.columnDef.meta?.filterOptions}
								value={header.column.getFilterValue()}
								onFilterChange={setFilterSelect}
							/>
						)}
						{canFilter === "text" && (
							<TextFilter
								value={header.column.getFilterValue()}
								onFilterChange={(value) => header.column.setFilterValue(value)}
							/>
						)}
					</PopoverContent>
				</Popover>
			)}
		</TableHead>
	);
}
interface SortOptionProps {
	readonly onToggleSort: (value: "asc" | "desc") => () => void;
	readonly sortTo: "asc" | "desc" | "none";
}
function SortOptions(props: SortOptionProps) {
	const { onToggleSort, sortTo } = props;
	return (
		<>
			<MenuItem
				onClick={onToggleSort("asc")}
				style={sortTo === "asc" ? { fontWeight: "bold" } : {}}
			>
				<SortUp />
				Asc
			</MenuItem>
			<MenuItem
				onClick={onToggleSort("desc")}
				style={sortTo === "desc" ? { fontWeight: "bold" } : {}}
			>
				<SortDown />
				Desc
			</MenuItem>
		</>
	);
}

interface SelectFilterOptionsProps {
	readonly options: ColumnMeta<CommonFeatures, object>["filterOptions"]; // Options for select filters
	readonly value: unknown;
	readonly onFilterChange: (value: string) => void;
}
function SelectFilterOptions(props: SelectFilterOptionsProps) {
	const { options, value, onFilterChange } = props;
	if (options == null) throw new Error("No options provided for select filter");
	if (options.length === 0) return null;
	const optionsConverted = Array.isArray(options)
		? options
		: Object.entries(options);

	return (
		<>
			<Separator />
			{optionsConverted.map((option) => (
				<MenuItem
					key={option[0]}
					onClick={() => onFilterChange(option[0])}
					style={value === option[0] ? { fontWeight: "bold" } : {}}
				>
					<DotIcon />
					{option[1]}
				</MenuItem>
			))}
		</>
	);
}

interface TextFilterProps {
	readonly onFilterChange: (value: string) => void;
	readonly value: unknown;
}
function TextFilter(props: TextFilterProps) {
	const { onFilterChange, value } = props;

	if (typeof value !== "string" && value != null) {
		throw new Error("Text filter value must be a string or null");
	}

	return (
		<>
			<Separator />
			<div onClick={(e) => e.preventDefault()}>
				<input
					type="text"
					value={value ?? ""}
					onChange={(e) => onFilterChange(e.target.value)}
					placeholder="Filtrar..."
					className="w-full bg-transparent outline-none"
				/>
			</div>
		</>
	);
}

function MenuItem(props: React.HTMLProps<HTMLDivElement>) {
	return (
		<div
			className="flex gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground py-2"
			{...props}
		>
			{props.children}
		</div>
	);
}

function NormalHead<TData extends RowData>({ header }: { readonly header: Header<CommonFeatures, TData>; }) {
	return (
		<TableHead colSpan={header.colSpan}>
			{/* Handles all possible header column def scenarios for `header` */}
			{flexRender(header.column.columnDef.header, header.getContext())}
		</TableHead>
	);
}
